/**
 * The parse-activity panel's "Details" preference: plain English, or the
 * technical tier (model, effort, route key, timings, raw tool arguments).
 *
 * **Sticky, and deliberately per-device.** Someone who wants to watch the call
 * tree wants it on every parse, not once; and unlike the summary-card prefs
 * (`components/summaries/useSummaryCardPrefs.ts`) this one is not worth a
 * server round-trip per page load or a new column — it is a single boolean
 * about how to draw a transient indicator, and getting it wrong costs one
 * click. `localStorage` is the right size of store for that, with the cost
 * that the box's three HTTPS clone origins each remember it separately.
 *
 * **It seeds a panel; it does not drive one.** This used to be a module-level
 * value plus a subscriber set, so every mounted panel flipped together — which
 * is fine until two analyses are open at once, and then reading the call tree
 * of one silently switches the other out from under you (reported
 * 2026-09-23). A tier is a fact about the panel you are reading, so each panel
 * owns its own and this is only the value a *new* one starts at. Toggling one
 * still writes here, so the next panel — and the next page load — opens the way
 * you last left it, which is the whole of what "sticky" was for.
 *
 * Every storage touch is wrapped — a private window, a blocked-cookies profile
 * or a thumbnail capture can make the accessor throw, and the honest fallback
 * is the plain tier, which is what someone who has never expressed a
 * preference sees anyway.
 */

export const TECHNICAL_TIER_KEY = 'honeyguideParseActivityTechnical'

/** Read the stored preference. Anything unreadable or unrecognized is `false`. */
export const readStoredTechnicalTier = (): boolean => {
    try {
        return window.localStorage.getItem(TECHNICAL_TIER_KEY) === 'true'
    } catch {
        return false
    }
}

let current = readStoredTechnicalTier()

/** What a panel opening now should start at. */
export const getTechnicalTier = (): boolean => current

/**
 * Remember the tier for the panels that open next, and for the next page load.
 * Deliberately tells nobody: the panel that was clicked already knows, and any
 * other panel on the page is a different parse the reader did not touch. A
 * write that cannot be persisted still moves the in-memory default, because
 * losing the across-reloads half is not a reason to also lose the rest of this
 * session.
 */
export const setTechnicalTier = (value: boolean): void => {
    if (value === current) return
    current = value
    try {
        window.localStorage.setItem(TECHNICAL_TIER_KEY, value ? 'true' : 'false')
    } catch {
        /* private window, blocked site data, or no storage at all */
    }
}
